/**
 * TrainingLayout — the Training section's page shell inside the real App chrome.
 *
 * Deliberately leaner than SettingsLayout: Training is per-user curriculum, not
 * fleet data, so there is no unit StatusBar, no filter banner, and no filter
 * provider — just the App Header, the grouped desktop sidebar, and the routed
 * outlet. On mobile the sidebar is replaced by the Header's hamburger menu (which
 * carries the Training section), matching every other section.
 *
 * Sidebar-collapsed is persisted in localStorage (`training_sidebar_collapsed`),
 * exactly as SettingsLayout persists its own. The DB-backed move onto the user
 * profile lands with the progress store in TR-5.
 */
import { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { Header } from './Header';
import { TrainingSidebar } from './TrainingSidebar';
import { TrainingMobileTabBar } from './TrainingMobileTabBar';
import {
  TrainingProgressProvider,
  useTrainingProgress,
} from '@/pages/training/TrainingProgressContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import '@/pages/training/training-design.css';
import '@/pages/training/training-shell.css';

const SIDEBAR_KEY = 'training_sidebar_collapsed';

/**
 * Reads live progress from the context (it's rendered under the provider) and
 * feeds the sidebar's Learn/Cards/Missed badges. Kept separate so TrainingSidebar
 * stays presentational (and testable without the provider).
 */
function ConnectedSidebar({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: (collapsed: boolean) => void;
}) {
  const p = useTrainingProgress();
  return (
    <TrainingSidebar
      collapsed={collapsed}
      onToggle={onToggle}
      progress={{
        lessonsDone: Object.keys(p.lessonsDone).length,
        cardsKnown: Object.keys(p.cardKnown).length,
        missed: p.missedList().length,
      }}
    />
  );
}

/**
 * Shown when progress can't be saved for a reason the user can fix: they belong to
 * several companies and have no default, so the server won't guess which company
 * their progress belongs to. Studying still works (in-memory) — it just won't persist.
 */
function SaveBlockedNotice() {
  const { saveBlocked } = useTrainingProgress();
  if (saveBlocked !== 'no_active_company') return null;
  return (
    <div className="warnbox" role="status" style={{ marginBottom: 16 }}>
      <strong>Your progress isn&apos;t being saved.</strong> You belong to more than one company —
      set a default company in <Link to="/settings/profile">Settings → My Profile</Link> and
      reload to save your training progress.
    </div>
  );
}

export function TrainingLayout() {
  const isMobile = useIsMobile();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem(SIDEBAR_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, String(isSidebarCollapsed));
    } catch {
      /* storage unavailable (private mode) — collapse just won't persist */
    }
  }, [isSidebarCollapsed]);

  return (
    <TrainingProgressProvider>
      <div className="h-dvh flex flex-col overflow-hidden bg-background">
        <div className="flex-none z-40">
          <Header />
        </div>

        {/* `.layout` MUST be a DESCENDANT of `.cdl-scope`: every vendored rule is
            scoped as `.cdl-scope .layout {display:flex}` etc. With both classes on
            one element the rule never matched, so the sidebar and <main> stacked
            vertically and the content fell below this overflow-hidden region. */}
        <div className="cdl-scope flex-1 min-h-0 overflow-hidden">
          <div className="layout">
            {!isMobile && (
              <ConnectedSidebar collapsed={isSidebarCollapsed} onToggle={setIsSidebarCollapsed} />
            )}
            <main className="main overflow-y-auto">
              <SaveBlockedNotice />
              <Outlet />
            </main>
          </div>
        </div>

        {/* Mobile: the design's bottom tab bar replaces the desktop sidebar */}
        {isMobile && <TrainingMobileTabBar />}
      </div>
    </TrainingProgressProvider>
  );
}
